import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemText,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Stack,
  ListItemIcon,
  Badge,
  Collapse,
  ListItemButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import BuildIcon from '@mui/icons-material/Build';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import WebIcon from '@mui/icons-material/Web';
import LaptopIcon from '@mui/icons-material/Laptop';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import InfoIcon from '@mui/icons-material/Info';
import CloudIcon from '@mui/icons-material/Cloud';
import ShieldIcon from '@mui/icons-material/Shield';
import RouterIcon from '@mui/icons-material/Router';
import DevicesIcon from '@mui/icons-material/Devices';
import DnsIcon from '@mui/icons-material/Dns';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import PhoneIcon from '@mui/icons-material/Phone';
import { useCart } from '../context/CartContext';
import { isAdminLoggedIn } from '../utils/auth';
import Logo from './Logo';

const navItems = [
  { text: 'Home',     path: '/',         icon: HomeIcon },
  { text: 'Services', path: '/services', icon: BuildIcon },
  { text: 'Products', path: '/products', icon: null },
  { text: 'Pricing',  path: '/pricing',  icon: null },
  { text: 'Blog',     path: '/blog',     icon: null },
  { text: 'FAQ',      path: '/faq',      icon: null },
  { text: 'About',    path: '/about',    icon: InfoIcon },
  { text: 'Contact',  path: 'https://akdmetech.com/contact', icon: ContactSupportIcon, external: true },
];

const serviceIcons = {
  'Microsoft 365 & Azure': CloudIcon,
  'Cybersecurity': ShieldIcon,
  'Network Installation': RouterIcon,
  'Endpoint Management': DevicesIcon,
  'Server Administration': DnsIcon,
  'Software Install & Config': InstallDesktopIcon,
  'VoIP & Phone Systems': PhoneIcon,
  'Laptop Repair': LaptopIcon,
  'Desktop Service': ComputerIcon,
  'Mobile Device Repair': PhoneIphoneIcon,
  'Technical Support': SupportAgentIcon,
  'Website Development': WebIcon,
};

const StyledButton = styled(Button)(({ theme, active }) => ({
  color: '#ffe8e8',
  padding: '6px 10px',
  fontSize: '0.875rem',
  fontWeight: active ? 600 : 500,
  borderRadius: '8px',
  minWidth: 'unset',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.25s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: active
      ? 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)'
      : 'transparent',
    opacity: active ? 1 : 0,
    transition: 'opacity 0.25s ease',
    zIndex: -1,
  },
  '&:hover': {
    transform: 'translateY(-1px)',
    '&::before': {
      opacity: 0.8,
    },
  },
}));

const BookButton = styled(Button)(({ theme, active }) => ({
  color: '#ffe8e8',
  padding: '8px 18px',
  fontSize: '0.9rem',
  fontWeight: 700,
  letterSpacing: '0.3px',
  borderRadius: '10px',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.25s ease',
  background: active
    ? 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)'
    : 'linear-gradient(135deg, #FE6B8B 0%, #FF8E53 100%)',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  boxShadow: '0 2px 12px rgba(255,107,107,0.35)',
  '& .MuiButton-startIcon': {
    marginRight: '6px',
    marginTop: 0,
  },
  '& .MuiButton-startIcon > *:nth-of-type(1)': {
    fontSize: '1.1rem',
  },
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 18px rgba(255,107,107,0.5)',
    opacity: 0.92,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '7px 14px',
    fontSize: '0.85rem',
  },
}));

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall  = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const { getCartCount } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleServicesClick = () => {
    setServicesOpen(!servicesOpen);
  };

  const handleAdminAccess = () => {
    if (isAdminLoggedIn()) {
      navigate('/admin/dashboard');
    } else {
      navigate('/admin/login');
    }
    if (mobileOpen) {
      handleDrawerToggle();
    }
  };

  const drawer = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        background: 'rgba(45, 52, 54, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRight: '1px solid rgba(255, 107, 107, 0.1)',
        pt: 2,
      }}
    >
      <List>
        {navItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItemButton
              component={item.external ? 'a' : RouterLink}
              {...(item.external ? { href: item.path, target: '_blank', rel: 'noopener noreferrer' } : { to: item.path })}
              onClick={() => {
                if (item.text === 'Services') {
                  handleServicesClick();
                } else {
                  handleDrawerToggle();
                }
              }}
              sx={{
                py: 2,
                px: 3,
                background: location.pathname === item.path ? 'rgba(255, 107, 107, 0.1)' : 'transparent',
                '&:hover': {
                  background: 'rgba(255, 107, 107, 0.1)',
                },
              }}
            >
              {item.icon && (
                <ListItemIcon sx={{ color: '#ffe8e8', minWidth: 40 }}>
                  <item.icon />
                </ListItemIcon>
              )}
              <ListItemText 
                primary={item.text} 
                sx={{ 
                  color: '#ffe8e8',
                  '& .MuiListItemText-primary': {
                    fontSize: '1.1rem',
                    fontWeight: location.pathname === item.path ? 600 : 400,
                  },
                }}
              />
              {item.text === 'Services' && (
                servicesOpen ? <ExpandLess sx={{ color: '#ffe8e8' }} /> : <ExpandMore sx={{ color: '#ffe8e8' }} />
              )}
            </ListItemButton>
            {item.text === 'Services' && (
              <Collapse in={servicesOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {[
                    { label: 'Microsoft 365 & Azure', path: '/microsoft-365-cloud' },
                    { label: 'Cybersecurity', path: '/cybersecurity' },
                    { label: 'Network Installation', path: '/network-installation' },
                    { label: 'Endpoint Management', path: '/endpoint-management' },
                    { label: 'Server Administration', path: '/server-administration' },
                    { label: 'Software Install & Config', path: '/software-installation' },
                    { label: 'VoIP & Phone Systems', path: '/voip-systems' },
                    { label: 'Laptop Repair', path: '/laptop-repair' },
                    { label: 'Desktop Service', path: '/desktop-service' },
                    { label: 'Mobile Device Repair', path: '/mobile-repair' },
                    { label: 'Technical Support', path: '/technical-support' },
                    { label: 'Website Development', path: '/website-development' },
                  ].map(({ label, path }) => {
                    const ServiceIcon = serviceIcons[label];
                    return (
                      <ListItemButton
                        key={label}
                        component={RouterLink}
                        to={path}
                        onClick={handleDrawerToggle}
                        sx={{
                          pl: 7,
                          py: 1.5,
                          background: location.pathname === path
                            ? 'rgba(255, 107, 107, 0.1)'
                            : 'transparent',
                          '&:hover': {
                            background: 'rgba(255, 107, 107, 0.1)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ color: '#ffe8e8', minWidth: 40 }}>
                          {ServiceIcon && <ServiceIcon />}
                        </ListItemIcon>
                        <ListItemText
                          primary={label}
                          sx={{
                            color: '#ffe8e8',
                            '& .MuiListItemText-primary': {
                              fontSize: '1rem',
                            },
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
        <ListItemButton
          onClick={handleAdminAccess}
          sx={{
            py: 2,
            px: 3,
            background: isAdminLoggedIn() ? 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)' : 'transparent',
            '&:hover': {
              background: isAdminLoggedIn() 
                ? 'linear-gradient(135deg, #ff5252 0%, #ffd93d 100%)'
                : 'rgba(255, 107, 107, 0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ color: '#ffe8e8', minWidth: 40 }}>
            <AdminPanelSettingsIcon />
          </ListItemIcon>
          <ListItemText 
            primary={isAdminLoggedIn() ? "Admin Dashboard" : "Admin Login"}
            sx={{ 
              color: '#ffe8e8',
              '& .MuiListItemText-primary': {
                fontSize: '1.1rem',
              }
            }} 
          />
        </ListItemButton>
        <ListItemButton
          component={RouterLink}
          to="/cart"
          onClick={handleDrawerToggle}
          sx={{
            py: 2,
            px: 3,
          }}
        >
          <ListItemIcon sx={{ color: '#ffe8e8', minWidth: 40 }}>
            <Badge badgeContent={getCartCount()} color="error">
              <ShoppingCartIcon />
            </Badge>
          </ListItemIcon>
          <ListItemText 
            primary="Cart"
            sx={{ 
              color: '#ffe8e8',
              '& .MuiListItemText-primary': {
                fontSize: '1.1rem',
              }
            }} 
          />
        </ListItemButton>
        <ListItemButton component={RouterLink} to="/quote" onClick={handleDrawerToggle} sx={{ py: 2, px: 3 }}>
          <ListItemIcon sx={{ color: '#ffe8e8', minWidth: 40 }}><EventAvailableIcon /></ListItemIcon>
          <ListItemText primary="Get a Quote" sx={{ color: '#ffe8e8', '& .MuiListItemText-primary': { fontSize: '1.1rem' } }} />
        </ListItemButton>
        <ListItemButton component={RouterLink} to="/track-repair" onClick={handleDrawerToggle} sx={{ py: 2, px: 3 }}>
          <ListItemIcon sx={{ color: '#ffe8e8', minWidth: 40 }}><BuildIcon /></ListItemIcon>
          <ListItemText primary="Track Repair" sx={{ color: '#ffe8e8', '& .MuiListItemText-primary': { fontSize: '1.1rem' } }} />
        </ListItemButton>
        <ListItemButton
          component={RouterLink}
          to="/booking"
          onClick={handleDrawerToggle}
          sx={{
            py: 2,
            px: 3,
            background: location.pathname === '/booking' ? 'rgba(255, 107, 107, 0.1)' : 'transparent',
          }}
        >
          <ListItemIcon sx={{ color: '#ffe8e8', minWidth: 40 }}>
            <EventAvailableIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Book Service"
            sx={{ 
              color: '#ffe8e8',
              '& .MuiListItemText-primary': {
                fontSize: '1.1rem',
              }
            }} 
          />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed"
        sx={{
          background: scrolled 
            ? 'rgba(17, 17, 17, 0.95)'
            : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          boxShadow: scrolled 
            ? '0 4px 30px rgba(255, 107, 107, 0.15)'
            : 'none',
          borderBottom: scrolled 
            ? '1px solid rgba(255, 107, 107, 0.1)'
            : 'none',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{
              height: { xs: 64, md: scrolled ? 64 : 72 },
              transition: 'all 0.3s ease-in-out',
              px: { xs: 1.5, md: 2 },
              gap: { xs: 1, md: 1.5 },
            }}
          >
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  mr: 1,
                  color: '#ffe8e8',
                  '&:hover': {
                    background: 'rgba(255, 107, 107, 0.1)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <RouterLink 
              to="/" 
              style={{ 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Logo size={isMobile ? "small" : "small"} />
            </RouterLink>

            {!isMobile && (
              <Stack
                direction="row"
                spacing={0}
                sx={{
                  flexGrow: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {navItems.map((item) => (
                  <StyledButton
                    key={item.text}
                    component={item.external ? 'a' : RouterLink}
                    {...(item.external ? { href: item.path, target: '_blank', rel: 'noopener noreferrer' } : { to: item.path })}
                    active={location.pathname === item.path ? 1 : 0}
                  >
                    {item.text}
                  </StyledButton>
                ))}
              </Stack>
            )}

            <Box sx={{ flexGrow: isMobile ? 1 : 0 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Admin icon — hidden on xs to save space; accessible via drawer */}
              {!isSmall && (
                <IconButton
                  onClick={handleAdminAccess}
                  size="small"
                  sx={{
                    color: '#ffe8e8',
                    width: 36, height: 36,
                    background: isAdminLoggedIn() ? 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)' : 'transparent',
                    '&:hover': {
                      background: isAdminLoggedIn()
                        ? 'linear-gradient(135deg, #ff5252 0%, #ffd93d 100%)'
                        : 'rgba(255, 107, 107, 0.1)',
                    },
                  }}
                  aria-label={isAdminLoggedIn() ? 'Admin Dashboard' : 'Admin Login'}
                >
                  <AdminPanelSettingsIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
              <IconButton
                component={RouterLink}
                to="/cart"
                size="small"
                sx={{
                  color: '#ffe8e8',
                  width: 36, height: 36,
                  '&:hover': { background: 'rgba(255, 107, 107, 0.1)' },
                }}
              >
                <Badge badgeContent={getCartCount()} color="error">
                  <ShoppingCartIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Box>

            {/* Book button — icon-only on xs, short label on sm-md, full label on md+ */}
            {isSmall ? (
              <IconButton
                component={RouterLink}
                to="/booking"
                sx={{
                  background: 'linear-gradient(135deg, #FE6B8B 0%, #FF8E53 100%)',
                  color: '#fff',
                  width: 36, height: 36,
                  '&:hover': { opacity: 0.85 },
                }}
              >
                <EventAvailableIcon sx={{ fontSize: 20 }} />
              </IconButton>
            ) : (
              <BookButton
                component={RouterLink}
                to="/booking"
                startIcon={<EventAvailableIcon />}
                active={location.pathname === '/booking' ? 1 : 0}
              >
                {isMobile ? 'Book' : 'Book Repair'}
              </BookButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
          }
        }}
      >
        {drawer}
      </Drawer>

      <Toolbar
        sx={{
          height: { xs: 64, md: scrolled ? 64 : 72 },
          transition: 'all 0.3s ease-in-out',
        }}
      />
    </>
  );
}

export default Navbar;
